import githubMark from '@/assets/images/github-mark.svg'
import googleLogo from '@/assets/images/google-logo.svg'
import microsoftLogo from '@/assets/images/microsoft-logo.svg'
import { FeaturesInfoPanel } from '@/components/FeaturesInfoPanel'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { useLogin } from '@/hooks/useAuth'
import { useErrorHandler } from '@/hooks/useErrorHandler'
import { cn } from '@/lib/utils'
import type { LoginCredentials } from '@/stores/auth'
import { useAuthStore } from '@/stores/authStore'
import { useLocation } from '@tanstack/react-router'
import { useState } from 'react'
import { Spinner } from './ui/spinner'

interface LoginFormProps {
	onSuccess?: () => void
	onError?: (error: string) => void
}

export function LoginForm({ onSuccess, onError }: LoginFormProps) {
	const [isLoading, setIsLoading] = useState(false)
	const loginMutation = useLogin()
	const location = useLocation()
	const companyName = 'Bytebeam'

	const { handleError } = useErrorHandler()

	const handleLogin = async (provider: LoginCredentials['provider']) => {
		setIsLoading(true)

		try {
			const redirectUri = (location.search as { redirect?: string })?.redirect || '/'
			await loginMutation.mutateAsync({ provider, redirectUri })
			onSuccess?.()
		} catch (error: unknown) {
			const apiError = handleError(error, {
				showToast: false,
			})
			onError?.(apiError.message || 'Login failed')
		} finally {
			setIsLoading(false)
		}
	}

	const loginButtons = [
		{ provider: 'google' as const, label: 'Google', icon: googleLogo },
		{
			provider: 'microsoft' as const,
			label: 'Microsoft',
			icon: microsoftLogo,
		},
		{ provider: 'github' as const, label: 'GitHub', icon: githubMark },
	]
	const lastUsedProvider = useAuthStore(state => state.lastUsedProvider)

	return (
		<div className='flex min-h-screen min-w-screen items-center justify-center bg-background px-4 py-12'>
			<div className='w-full max-w-3xl'>
				<Card className='overflow-hidden bg-card shadow-lg'>
					<CardContent className='grid grid-cols-1 md:grid-cols-2 p-0'>
						{/* Left: FeaturesInfo panel */}
						<FeaturesInfoPanel />

						{/* Right: Login form */}
						<div className='p-6 md:p-8 flex flex-col justify-center'>
							<div className='flex flex-col justify-between h-full gap-10'>
								<h1 className='text-form-modal-title font-bold'>Login to {companyName}</h1>

								<div className='grid gap-5'>
									{loginButtons.map(({ provider, label, icon }) => (
										<Button
											key={provider}
											variant='outline'
											className='relative flex items-center justify-start h-10 px-8 space-x-4'
											disabled={isLoading || loginMutation.isPending}
											onClick={() => handleLogin(provider)}>
											<img
												src={icon}
												alt={label}
												className={cn('size-(--icon-standalone)', provider === 'github' && 'dark:invert')}
											/>
											{isLoading || loginMutation.isPending ? (
												<div className='relative flex items-center'>
													<Spinner className='mr-2 text-primary' />
													Signing in...
												</div>
											) : (
												<span>Continue with {label}</span>
											)}
											{lastUsedProvider === provider && (
												<span className='absolute top-[-12px] right-[-14px] m-1 px-2 py-0.5 text-badge bg-primary text-primary-foreground rounded'>
													LAST
												</span>
											)}
										</Button>
									))}
									{loginMutation.error && (
										<div className='rounded-md bg-destructive/10 p-3 text-body text-destructive'>
											{loginMutation.error.message}
										</div>
									)}
								</div>

								{/* Legal */}
								<div className='text-body space-y-3'>
									<p>
										By continuing, you agree to our{' '}
										<a
											href='https://www.bytebeam.io/terms-of-service'
											target='_blank'
											rel='noreferrer'
											className='underline text-primary'>
											Terms of Service
										</a>{' '}
										and{' '}
										<a
											href='https://www.bytebeam.io/privacy-policy'
											target='_blank'
											rel='noreferrer'
											className='underline text-primary'>
											Privacy Policy
										</a>
										.
									</p>
								</div>
							</div>
						</div>
					</CardContent>
				</Card>

				<footer className='mt-8 text-center text-body text-muted-foreground'>
					Copyright &copy; {new Date().getFullYear()} IoTExpress Technologies Pvt. Ltd. All rights reserved.
				</footer>
			</div>
		</div>
	)
}
